/**
 * Protected Route
 * Route guard that requires authentication.
 * Identity = access token (httpOnly cookie, not readable in JS). Workspace = context token.
 * On onboarding routes (onboarding, accept-invites, branch-setup) we allow access-token-only users;
 * backend validates the cookie on API calls.
 */

/** Routes where authenticated user may have no workspace context yet (access token only). */
const ROUTES_ALLOW_ACCESS_TOKEN_ONLY = ['/onboarding', '/accept-invites', '/branch-setup']

function isAccessTokenOnlyRoute(path: string): boolean {
  if (ROUTES_ALLOW_ACCESS_TOKEN_ONLY.includes(path)) return true
  return /^\/[^/]+\/branch-setup$/.test(path)
}

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { PageLoader } from '@/components/loaders/PageLoader'
import { authService } from '@/services/api/shared/auth.service'
import { tokenManager } from '@/utils/tokenManager'
import { navigationService } from '@/services/api/shared/navigation.service'
import { useEffect, useState, useRef } from 'react'
import { useConfirmationDialog } from '@/components/modals/confirmation-dialog'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(true)
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false)
  const [isWaitingForInitialization, setIsWaitingForInitialization] = useState(true)
  const previousPath = useRef(location.pathname)
  const hasShownAlertRef = useRef(false)
  const [shouldNavigate, setShouldNavigate] = useState(false)
  const { confirm, dialog } = useConfirmationDialog()

  // Wait for AuthInitializer to complete before checking authentication
  useEffect(() => {
    // Cookies work automatically - no URL token checking needed
    // Check if we have context token or user in state (accessToken is httpOnly, not readable)
    const hasAnyToken = tokenManager.getToken('contextToken') || authService.getUser()
    if (hasAnyToken) {
      // We have tokens, wait a bit for AuthInitializer to restore state
      const timer = setTimeout(() => {
        setIsWaitingForInitialization(false)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      // No tokens, AuthInitializer should complete immediately
      setIsWaitingForInitialization(false)
    }
  }, [])

  // Check both Redux state and localStorage to handle race conditions
  useEffect(() => {
    // Don't check until AuthInitializer has had time to initialize
    if (isWaitingForInitialization) {
      return
    }

    // Only show loader on initial check or if path actually changed (not just re-render)
    const isPathChange = previousPath.current !== location.pathname
    
    // On initial mount or first check, wait a bit
    if (!hasCheckedOnce) {
      const timer = setTimeout(() => {
        setIsChecking(false)
        setHasCheckedOnce(true)
      }, 100)
      return () => clearTimeout(timer)
    } else if (isPathChange) {
      // Path changed - this is navigation, don't show loader
      previousPath.current = location.pathname
      setIsChecking(false)
    } else {
      // Same path, just re-render - don't show loader
      setIsChecking(false)
    }
  }, [location.pathname, isAuthenticated, isLoading, hasCheckedOnce, isWaitingForInitialization])

  // Handle token clearing with alert when user is not authenticated
  useEffect(() => {
    // Don't check until AuthInitializer has had time to initialize
    if (isWaitingForInitialization || ((isLoading || isChecking) && !hasCheckedOnce)) {
      return
    }

    // Check authentication state
    // Note: accessToken is in httpOnly cookie, not accessible via JavaScript
    const hasContextToken = tokenManager.getToken('contextToken')
    const hasUser = authService.getUser()
    const allowAccessTokenOnly = isAccessTokenOnlyRoute(location.pathname)
    // Consider authenticated if: Redux says so, OR we have contextToken, OR we have user in state,
    // OR we're on onboarding/accept-invites/branch-setup (access token in cookie is enough; backend validates)
    const isActuallyAuthenticated = isAuthenticated || hasContextToken || hasUser || allowAccessTokenOnly

    // If not authenticated and we have tokens, clear them with dialog
    if (!isActuallyAuthenticated && hasContextToken && !hasShownAlertRef.current) {
      hasShownAlertRef.current = true
      
      // Show dialog before clearing storage
      const currentDomain = window.location.hostname
      const currentPath = window.location.pathname
      confirm(
        'Authentication Required',
        `You are not authenticated. We will now clear all authentication tokens, localStorage, and cookies for the current domain (${currentDomain}) before redirecting to the login page.\n\nCurrent path: ${currentPath}`,
        () => {
          // UNCOMMENTED: Clear auth data
          tokenManager.clearAllAuthData()
          // Also clear sidebar menu as it's context-specific
          try {
            navigationService.clearStoredSidebarMenu()
          } catch (error) {
            // Silent error handling
          }
          
          // After dialog is dismissed and tokens are cleared, allow navigation
          setShouldNavigate(true)
        },
        {
          confirmLabel: 'Continue',
          cancelLabel: 'Cancel',
          variant: 'default' as const,
        }
      )
    } else if (!isActuallyAuthenticated && !hasContextToken) {
      // No tokens to clear, navigate immediately
      setShouldNavigate(true)
    }
  }, [isAuthenticated, isLoading, isChecking, hasCheckedOnce, isWaitingForInitialization])

  // Check authentication state
  // Note: accessToken is in httpOnly cookie, not accessible via JavaScript
  const hasContextToken = tokenManager.getToken('contextToken')
  const hasUser = authService.getUser()
  const allowAccessTokenOnly = isAccessTokenOnlyRoute(location.pathname)
  // Consider authenticated if: Redux says so, OR we have contextToken, OR we have user in state,
  // OR we're on onboarding/accept-invites/branch-setup (access token in cookie; backend validates on API call)
  const isActuallyAuthenticated = isAuthenticated || hasContextToken || hasUser || allowAccessTokenOnly

  // Show loader while waiting for initialization or checking
  if (isWaitingForInitialization || ((isLoading || isChecking) && !hasCheckedOnce)) {
    return <PageLoader />
  }

  if (!isActuallyAuthenticated) {
    // If no tokens exist, navigate immediately (no need for dialog)
    if (!hasContextToken) {
      return <Navigate to="/login" state={{ from: location }} replace />
    }
    
    // If tokens exist, wait for dialog to be shown and tokens to be cleared
    if (shouldNavigate) {
      return <Navigate to="/login" state={{ from: location }} replace />
    }
    
    // Show loader while waiting for dialog to be shown and tokens to be cleared
    return (
      <>
        <PageLoader />
        {dialog}
      </>
    )
  }

  return <>{children}</>
}

